import * as React from 'react';
import { Translate } from '../../../../../shared/LanguageService';
import { Button, Col, Panel, Row } from 'react-bootstrap';
import { IBasicOrderInfo, IBookingInfo, IPlanInfo, OrderTypes } from '../Types';
import * as Features from '../../../../../shared/Features';
import * as UnarchiveStatus from '../UnarchivedStatus';
import * as PhotoStatus from '../../../../../shared/PhotoStatus';
import * as OrderState from '../OrderState';
import * as PlanOrderStatus from '../../planOrders/PlanOrderStatus';
import PopUpButton from '../../../../../components/common/PopUpButton';
import DateTimeView from '../../../../localDate/DateTimeView';
import { BrokerAgentWithDropDown, BrokerFirmWithDropDown } from '../../common/BrokerTreeElements';
import IEnumerationItem from '../../../../../shared/interfaces/IEnumerationItem';
import * as ArchiveStatus from '../../../../../shared/ArchiveStatus';
import * as PlanOrderCombinedStatus from '../../planOrders/PlanOrderCombinedStatus';
import PhotoOrderInfo from './OrderTypes/PhotoOrderInfo';
import StylingOrderInfo from './OrderTypes/StylingOrderInfo';
import { isDigitalStylingOrder, isPhotoOrder, isStylingOrder } from '../../../utils/orderUtils';
import moment from 'moment';
import DigitalStylingOrderInfo from '../digitalStylingOrderInfo/view/DigitalStylingOrderInfo';
import InvoiceInformation from './invoiceInformation/view/InvoiceInformation';
import { AuthenticatedUser, UserRole } from '../../../../../shared/types';
import { BrokerSystemModal } from './brokerSystemModal/BrokerSystemModal';
import { getSupportedContentProvidersForManualConnection } from '../../../../../shared/ContentProvider';
import SuccessSnackbar from '../../../../../shared/feedback/SuccessSnackbar';
import FailureSnackbar from '../../../../../shared/feedback/FailureSnackbar';
import BookingRequestNotice from './BookingRequestNotice';


interface IProps {
    order: IBasicOrderInfo;
    site: string;
    orderId: number;
    user: any;
    navigateTo: (url: string) => void;
    showArchiveSampleImagesModal: boolean;
    onToggleArchiveSampleImagesModal: (show: boolean) => void;
    digitalStylingOrderStatus: { displayName: string; value: number } | null;
    unbookPhotographer: () => void;
    markAsUnread: () => void;
    unarchivePhoto: () => void;
    unarchivePlan: () => void;
    onIsDirectDeliveryChanged: (e: any) => void;
    updateImageEditorFirmChanged: (e: any) => void;
    updateImageEditorChanged: (e: any) => void;
    setOrderToUploader: () => void;
    updateOrderState: (orderState: number) => void;
    lockPhotographer: () => void;
    unlockPhotographer: () => void;
    saveComment: (text: string, type: number) => void;
    onBrokerSystemConnectionSaved: () => Promise<any>;}


const VIEW_INVOICE_ROLES = [
    UserRole.Admin,
    UserRole.Support,
    UserRole.Economist,
    UserRole.Seller
];
function canSeeInvoiceInformation(user: AuthenticatedUser): boolean {
    return VIEW_INVOICE_ROLES.includes(user.details.role);
}

const CUSTOMER_ROLES = [
    UserRole.BrokerAgent,
    UserRole.BrokerAgentAdmin,
    UserRole.BrokerAgentAssistant
];
function isCustomerRole(user: AuthenticatedUser): boolean {
    return CUSTOMER_ROLES.includes(user.details.role);
}

const OrderInfo = (props: IProps) => {
    const [showBrokerSystemModal, setShowBrokerSystemModal] = React.useState(false);
    const [showSuccessSnackbar, setShowSuccessSnackbar] = React.useState(false);
    const [showFailureSnackbar, setShowFailureSnackbar] = React.useState(false);
    let { user } = props;

    let shouldShowInvoiceInformation = canSeeInvoiceInformation(user);

    function getStartDateTitle() {
        if (props.order.createdAt && props.order.createdAt.localDateTime) {
            return `${Translate('view-order/basic-order-info/titles/order-arrival-date')}: ${moment(
                props.order.createdAt.localDateTime
            ).format('L LT')}`;
        }
        return '';
    }

    function planOrderIsArchived() {
        return (
            props.order.planInfo.archiveStatus &&
            props.order.planInfo.archiveStatus.value > ArchiveStatus.ArchivedCold
        );
    }

    const labels = {
        customerPhotoStatus: Translate('photo-order/status/customer-under-production')
    };

    return (
        <>
        <Col xs={12} md={7} lg={8}>
            <Col xs={12} lg={6}>
                <Panel>
                    <Panel.Heading>
                        <h3 className="panel-title">
                            {Translate('view-order/basic-order-info/panel-headers/order-info')}
                        </h3>
                    </Panel.Heading>
                    <Panel.Body>
                        <dl>
                            {props.order.startDate && (
                                <div>
                                    <dt>
                                        {Translate('view-order/basic-order-info/titles/start-date')}
                                    </dt>
                                    <dd title={getStartDateTitle()}>
                                        <DateTimeView dateTime={props.order.startDate} />
                                    </dd>
                                </div>
                            )}
                            {!props.order.startDate && (
                                <div>
                                    <dt>
                                        {Translate(
                                            'view-order/basic-order-info/titles/order-arrival-date'
                                        )}
                                    </dt>
                                    <dd>
                                        <DateTimeView dateTime={props.order.createdAt} />
                                    </dd>
                                </div>
                            )}

                            <dt>{Translate('view-order/basic-order-info/titles/customer')}</dt>
                            <Row>
                                <dd>
                                    <Col sm={12}>
                                        <BrokerFirmWithDropDown
                                            user={props.user}
                                            name={props.order.customer.firmName}
                                            brokerTreeNodeId={
                                                props.order.customer.firmBrokerTreeNodeId
                                            }
                                            navigateTo={props.navigateTo}
                                        />
                                    </Col>
                                    <Col sm={12} style={{ marginBottom: '7px' }}>
                                        <BrokerAgentWithDropDown
                                            id={props.order.customer.agentId}
                                            user={props.user}
                                            name={props.order.customer.agentName}
                                            brokerTreeNodeId={
                                                props.order.customer.agentBrokerTreeNodeId
                                            }
                                            navigateTo={props.navigateTo}
                                        />
                                    </Col>
                                </dd>
                            </Row>
                            <Row>
                                <Col sm={6}>
                                    <dt>
                                        {Translate(
                                            'view-order/basic-order-info/titles/content-provider'
                                        )}
                                    </dt>
                                    {!props.order.contentProvider && getSupportedContentProvidersForManualConnection().length > 0 && (
                                        <dd>
                                            <Button onClick={() => setShowBrokerSystemModal(true)}
                                                    bsSize="small" 
                                                    style={{ whiteSpace: 'normal' }}>
                                                    {Translate(
                                                        'view-order/basic-order-info/button/connect-broker-system'
                                                    )}
                                                </Button>
                                            {showBrokerSystemModal && (
                                            <BrokerSystemModal
                                            show={showBrokerSystemModal}
                                            onHide={() => setShowBrokerSystemModal(false)}
                                            brokerFirmContentProvider={props.order.customer.brokerFirmContentProvider}
                                            brokerFirmContentProviderId={props.order.customer.brokerFirmContentProviderId}
                                            orderId={props.orderId}
                                            onSaveSuccess={props.onBrokerSystemConnectionSaved}
                                            onSaveResult={(success) => success ? setShowSuccessSnackbar(true) : setShowFailureSnackbar(true)}
                                            />
                                            )}
                                        </dd>
                                    )}
                                    {props.order.contentProvider && (
                                        <dd>
                                            <PopUpButton
                                                title={'Mäklarsysteminfo'}
                                                buttonText={props.order.contentProvider}
                                                popUpText={
                                                    <div>
                                                        <p>
                                                            {Translate(
                                                                'view-order/basic-order-info/titles/object-id'
                                                            )}
                                                            : {props.order.objectId}
                                                        </p>
                                                        {props.order.objectNumber && (
                                                            <p>
                                                                Objektnummer:{' '}
                                                                {props.order.objectNumber}
                                                            </p>
                                                        )}
                                                    </div>
                                                }
                                            />
                                        </dd>
                                    )}
                                </Col>
                                <Col sm={6}>
                                    <dt>{Translate('words/order-status')}</dt>
                                    <dd>{Translate(props.order.orderState.displayName)}</dd>
                                </Col>
                            </Row>
                            {shouldShowInvoiceInformation ? (
                                <Row>
                                    <Col sm={12}>
                                        <InvoiceInformation
                                            user={props.user}
                                            orderId={props.orderId}
                                        />
                                    </Col>
                                </Row>
                            ) : null}
                            {Features.hasFeature(Features.UpdateOrderState, props.user) &&
                                ((props.order.bookingInfo && !props.order.bookingInfo.isArchived) ||
                                    (props.order.planInfo && !props.order.planInfo.isArchived) ||
                                    props.order.orderType === OrderTypes.digitalStyling) && (
                                    <dd>
                                        {props.order.orderState.value !== OrderState.Normal &&
                                            (props.order.orderState.value === OrderState.Removed ||
                                                props.order.orderState.value ===
                                                    OrderState.BlockedForBooking ||
                                                props.order.orderState.value ===
                                                    OrderState.BlockedForInvoice) && (
                                                <Button
                                                    bsStyle="success"
                                                    onClick={() =>
                                                        props.updateOrderState(OrderState.Normal)
                                                    }
                                                >
                                                    {Translate(
                                                        'view-order/basic-order-info/orderstate/normal/button'
                                                    )}
                                                </Button>
                                            )}
                                        {(((props.order.orderType & OrderTypes.plan) !== 0 &&
                                            (props.order.planInfo.sketchUpStatus.value ===
                                                PlanOrderStatus.created ||
                                                props.order.planInfo.roomSketcherStatus.value ===
                                                    PlanOrderStatus.created)) ||
                                            (props.order.bookingInfo &&
                                                !props.order.bookingInfo.bookedTime) ||
                                            (props.order.orderType === OrderTypes.digitalStyling &&
                                             (props.order.digitalStylingInfo &&
                                                !props.order.digitalStylingInfo.delivered))
                                        ) &&
                                            props.order.orderState.value !== OrderState.Removed && (
                                                <Button
                                                    bsStyle="danger"
                                                    onClick={() =>
                                                        props.updateOrderState(OrderState.Removed)
                                                    }
                                                >
                                                    {Translate(
                                                        'view-order/basic-order-info/orderstate/removed/button'
                                                    )}
                                                </Button>
                                            )}
                                        {(props.order.orderType & OrderTypes.bookable) !== 0 &&
                                            props.order.bookingInfo &&
                                            !props.order.bookingInfo.bookedTime &&
                                            props.order.orderState.value !==
                                                OrderState.BlockedForBooking && (
                                                <Button
                                                    bsStyle="warning"
                                                    onClick={() =>
                                                        props.updateOrderState(
                                                            OrderState.BlockedForBooking
                                                        )
                                                    }
                                                >
                                                    {Translate(
                                                        'view-order/basic-order-info/orderstate/blocked-for-booking/button'
                                                    )}
                                                </Button>
                                            )}
                                        {props.order.orderState.value !==
                                            OrderState.BlockedForInvoice && (
                                            <Button
                                                onClick={() =>
                                                    props.updateOrderState(
                                                        OrderState.BlockedForInvoice
                                                    )
                                                }
                                            >
                                                {Translate(
                                                    'view-order/basic-order-info/orderstate/blocked-for-invoice/button'
                                                )}
                                            </Button>
                                        )}
                                    </dd>
                                )}
                        </dl>
                        <BookingRequestNotice
                            orderId={props.orderId}
                            canBeMarkedAsHandled={
                                !!props.order.bookingInfo &&
                                props.order.bookingInfo.canBeMarkedAsHandled
                            }
                            user={props.user}
                        />
                    </Panel.Body>
                </Panel>
            </Col>
            {isPhotoOrder(props.order.orderType) && props.order.bookingInfo ? (
                <Col md={12} lg={6}>
                    <PhotoOrderInfo
                        bookingInfo={props.order.bookingInfo}
                        site={props.site}
                        user={props.user}
                        updateImageEditorFirmChanged={props.updateImageEditorFirmChanged}
                        updateImageEditorChanged={props.updateImageEditorChanged}
                        disableImageEditorFirm={disableImageEditorFirm}
                        availableImageEditorFirms={props.order.availableImageEditorFirms}
                        disableImageEditor={disableImageEditor}
                        availableImageEditors={props.order.availableImageEditors}
                        canChangeIsDirectDelivery={canChangeIsDirectDelivery}
                        onIsDirectDeliveryChanged={props.onIsDirectDeliveryChanged}
                        numberOfOrderRows={props.order.orderRows.length}
                        setOrderToUploader={props.setOrderToUploader}
                        markAsUnread={props.markAsUnread}
                        unarchivePhoto={props.unarchivePhoto}
                        onToggleArchiveSampleImagesModal={props.onToggleArchiveSampleImagesModal}
                        showArchiveSampleImagesModal={props.showArchiveSampleImagesModal}
                        unbookPhotographer={props.unbookPhotographer}
                        orderId={props.orderId}
                        navigateTo={props.navigateTo}
                        lockPhotographerAction={props.lockPhotographer}
                        lockPhotographerReason={lockPhotographerReason}
                        unlockPhotographer={props.unlockPhotographer}
                        labels={labels}
                        saveComment={props.saveComment} 
                    />
                </Col>
            ) : null}

            {(props.order.orderType & OrderTypes.plan) !== 0 ? (
                <Col md={6} lg={4} className="pull-right">
                    <Panel>
                        <Panel.Heading>
                            <h3 className="panel-title">
                                {Translate('view-order/basic-order-info/panel-headers/plan-info')}
                            </h3>
                        </Panel.Heading>
                        <Panel.Body>
                            <dl>
                                {renderPlanInfoStatusHeader(props.order.planInfo)}
                                {renderPlanInfoStatus(props.order.planInfo)}
                                <dt>
                                    {Translate(
                                        'view-order/basic-order-info/titles/plan-dimensions'
                                    )}
                                </dt>
                                <dd>{props.order.planInfo.dimensions}</dd>
                                {props.order.planInfo.cubiCasaStatus && !isCustomerRole(props.user) && (
                                        <div>
                                            <dt>
                                                {Translate(
                                                    'view-order/basic-order-info/titles/cubicasa-status'
                                                )}
                                            </dt>
                                            <dd>
                                                {Translate(
                                                    props.order.planInfo.cubiCasaStatus.displayName
                                                )}
                                            </dd>
                                        </div>
                                    )}
                                {props.order.planInfo.delivered && (
                                    <div>
                                        <dt>
                                            {Translate(
                                                'view-order/basic-order-info/titles/delivered'
                                            )}
                                        </dt>
                                        <dd>
                                            <DateTimeView
                                                dateTime={props.order.planInfo.delivered}
                                            />
                                        </dd>
                                    </div>
                                )}
                                {planOrderIsArchived() &&
                                    Features.hasFeature(Features.ViewArchivedOrder, props.user) && (
                                        <dt>
                                            {Translate(
                                                'view-order/basic-order-info/titles/archived'
                                            )}{' '}
                                            {Features.hasFeature(
                                                Features.ViewArchiveStatusForArchivedOrder,
                                                props.user
                                            ) && (
                                                <span>
                                                    (
                                                    {Translate(
                                                        props.order.planInfo.archiveStatus
                                                            .displayName
                                                    )}
                                                    )
                                                </span>
                                            )}
                                        </dt>
                                    )}
                                {planOrderIsArchived() &&
                                    Features.hasFeature(Features.ViewArchivedOrder, props.user) && (
                                        <dd>
                                            <DateTimeView
                                                dateTime={props.order.planInfo.dateArchived}
                                            />
                                            <br />

                                            {props.order.planInfo.canRestoreArchivedOrder &&
                                                !props.order.planInfo.unarchiveStatus &&
                                                Features.hasFeature(
                                                    Features.UnarchiveArchivedOrder,
                                                    props.user
                                                ) && (
                                                    <Button onClick={() => props.unarchivePlan()}>
                                                        {Translate(
                                                            'view-order/basic-order-info/button/unarchive'
                                                        )}
                                                    </Button>
                                                )}
                                        </dd>
                                    )}
                                {props.order.planInfo.unarchiveStatus && (
                                    <dt>
                                        {Translate('view-order/basic-order-info/titles/unarchive')}{' '}
                                        {Features.hasFeature(
                                            Features.ViewUnarchiveStatusForOrder,
                                            props.user
                                        ) &&
                                            props.order.planInfo.unarchiveStatus.value !==
                                                UnarchiveStatus.restored && (
                                                <span>
                                                    (
                                                    {Translate(
                                                        props.order.planInfo.unarchiveStatus
                                                            .displayName
                                                    )}
                                                    )
                                                </span>
                                            )}
                                    </dt>
                                )}
                                {props.order.planInfo.unarchiveStatus && (
                                    <dd>
                                        {(props.order.planInfo.unarchiveStatus.value ===
                                            UnarchiveStatus.glacierRetrieval ||
                                            props.order.planInfo.unarchiveStatus.value ===
                                                UnarchiveStatus.restoringFromCold ||
                                            props.order.planInfo.unarchiveStatus.value ===
                                                UnarchiveStatus.restoringFromGlacier) && (
                                            <div>
                                                {Translate(
                                                    'view-order/basic-order-info/unarchive-in-progress'
                                                )}
                                            </div>
                                        )}

                                        {props.order.planInfo.unarchiveStatus.value ===
                                            UnarchiveStatus.restored && (
                                            <div>
                                                {Translate(
                                                    'view-order/basic-order-info/info/rearchive',
                                                    {
                                                        rearchiveDate: props.order.planInfo
                                                            .rearchiveDate
                                                            ? moment(
                                                                  props.order.planInfo.rearchiveDate
                                                                      .localDateTime
                                                              ).format('L LT')
                                                            : ''
                                                    }
                                                )}
                                            </div>
                                        )}
                                    </dd>
                                )}
                            </dl>
                        </Panel.Body>
                    </Panel>
                </Col>
            ) : null}

            {isDigitalStylingOrder(props.order.orderType) && props.digitalStylingOrderStatus ? (
                <DigitalStylingOrderInfo
                    orderStatus={props.digitalStylingOrderStatus}
                    completedBy={
                        props.order.digitalStylingInfo
                            ? props.order.digitalStylingInfo.delivered
                            : null
                    }
                />
            ) : null}

            {isStylingOrder(props.order.orderType) && props.order.bookingInfo ? (
                <StylingOrderInfo
                    bookingInfo={props.order.bookingInfo}
                    orderId={props.orderId}
                    orderType={props.order.orderType}
                    site={props.site}
                    user={props.user}
                    markAsUnread={props.markAsUnread}
                    navigateTo={props.navigateTo}
                    unbookStylingUser={props.unbookPhotographer}
                />
            ) : null}
        </Col>
        <SuccessSnackbar
            open={showSuccessSnackbar}
            onClose={() => setShowSuccessSnackbar(false)}
            text={Translate('view-order/basic-order-info/modal/connection-success')}
        />
        <FailureSnackbar
            open={showFailureSnackbar}
            onClose={() => setShowFailureSnackbar(false)}
            text={Translate('view-order/basic-order-info/modal/connection-failure')}
        />
        </>
    );

    function disableImageEditorFirm(photoStatus: IEnumerationItem): boolean {
        if (
            photoStatus.value === PhotoStatus.Created ||
            photoStatus.value === PhotoStatus.Uploading ||
            photoStatus.value === PhotoStatus.UploaderApproved ||
            photoStatus.value === PhotoStatus.EditorUploading
        ) {
            return false;
        }
        return true;
    }

    function disableImageEditor(bookingInfo: IBookingInfo): boolean {
        return disableImageEditorFirm(bookingInfo.photoStatus) || !bookingInfo.imageEditorFirmId;
    }

    function lockPhotographer(bookingInfo: IBookingInfo) {
        return bookingInfo.customerWantsClient === true;
    }

    function lockPhotographerReason(bookingInfo: IBookingInfo): string {
        return lockPhotographer(bookingInfo)
            ? Translate('view-order/basic-order-info/button/rebook/disabled-title')
            : '';
    }

    function renderPlanInfoStatusHeader(planInfo: IPlanInfo) {
        if (Features.hasFeature(Features.ViewDetailedPlanOrderStatus, props.user)) {
            if (planInfo.sketchUpStatus && planInfo.roomSketcherStatus) {
                return (
                    <dt>
                        {Translate('view-order/basic-order-info/titles/plan-status-sketchup')} /{' '}
                        {Translate('view-order/basic-order-info/titles/plan-status-room-sketcher')}
                    </dt>
                );
            }
        }

        return <dt>{Translate('view-order/basic-order-info/titles/plan-status')}</dt>;
    }

    function renderPlanInfoStatus(planInfo: IPlanInfo) {
        if (Features.hasFeature(Features.ViewDetailedPlanOrderStatus, props.user)) {
            if (planInfo.sketchUpStatus && planInfo.roomSketcherStatus) {
                return (
                    <dd>
                        {Translate(planInfo.sketchUpStatus.displayName)} /{' '}
                        {Translate(planInfo.roomSketcherStatus.displayName)}
                    </dd>
                );
            }

            return <dd>{Translate(planInfo.sketchUpStatus.displayName)}</dd>;
        } else {
            return (
                <dd>
                    {planInfo.customerDeliveryAnnotation &&
                    planInfo.combinedStatus.value === PlanOrderCombinedStatus.Delivered &&
                    (props.user.details.role === 'role/broker-agent' ||
                        props.user.details.role === 'role/broker-agent-assistant' ||
                        props.user.details.role === 'role/broker-agent-admin') ? (
                        <span>
                            {Translate('view-order/basic-order-info/not-returned-annotations')}
                        </span>
                    ) : (
                        Translate(planInfo.combinedStatus.displayName)
                    )}
                </dd>
            );
        }
    }
};

function canChangeIsDirectDelivery(photoStatus: number) {
    return (
        photoStatus === PhotoStatus.Created ||
        photoStatus === PhotoStatus.Uploading ||
        photoStatus === PhotoStatus.UploaderApproved ||
        photoStatus === PhotoStatus.EditorUploading
    );
}

export default OrderInfo;
